import {
  ReportColumn as LibReportColumn,
  type OpenPersonReportInChatBVParams,
  type GenericReportResponse,
} from '@ltvco/refresh-lib/deepsearch';
import { RelatedReport } from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useLocation, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { getInitialVehicleReportPreferences } from 'components/reports/Preferences/getInitialVehicleReportPreferences';
import { ObituarySearchBanner } from 'components/reports/SearchBanners/ObituarySearchBanner';
import { PersonSearchBanner } from 'components/reports/SearchBanners/PersonSearchBanner';
import legitCallerGifUrl from 'images/caller_legit.gif';
import questionableCallerGifUrl from 'images/caller_questionable.gif';
import telemarketerCallerGifUrl from 'images/caller_telemarketer.gif';
import { getPersonNavLinkDataV2 } from 'navLinkData/personNavLinkData';
import { getPropertyNavLinkData } from 'navLinkData/propertyNavLinkData';
import { formatFamilyTreeDataFromPersonReport } from 'utils/formatFamilyTreeData';
import { getPropertyValuesCount } from 'utils/propertyReportUtils';

type ReportColumnProps = {
  onClose: () => void;
  permalink?: string;
  type?: string;
  currentWidth: string;
  reportData?: GenericReportResponse;
  handleOpenReportInChatBV?: (message: string) => void;
  handleOpenPersonReportInChatBV?: (
    params: OpenPersonReportInChatBVParams
  ) => void;
  handleRelatedReportClick?: (relatedReport: RelatedReport) => void;
};

const generateFullAddressFromParams = (
  queryParams: URLSearchParams
): string => {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';

  return `${address} ${city}, ${state} ${zipcode}`;
};

export const ReportColumn = (props: ReportColumnProps) => {
  const { hash, search } = useLocation();
  const navigate = useNavigate();

  // Feature flags
  const isInPartialAddressesVariation = useFeatureIsOn('OAR-1569');
  const isInStaggeredPhoneUpdateVariation = useFeatureIsOn('OAR-1352');
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const showUsernameSocialMedias = useFeatureIsOn('UsernameAPIService');
  const isInTaxHistoryVariation = useFeatureIsOn('prop-1877');
  const isInPropertySmartInsightsVariation = useFeatureIsOn('prop-2296');
  const isInDetailedHistoryVariation = useFeatureIsOn('bump-4397');
  const isInSummaryCardsVariation = useFeatureIsOn('bump-4414');
  const isInDamageHistory = useFeatureIsOn('bump-4464');
  const isInVehicleSpecsVariation = useFeatureIsOn('bump-4461');

  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';
  const fallbackPhone = queryParams.get('phone') || '';
  const fallbackAddress = generateFullAddressFromParams(queryParams);
  const fromParam = queryParams.get('from') || '';

  return (
    <LibReportColumn
      {...props}
      hash={hash}
      fallbackEmail={fallbackEmail}
      fallbackPhone={fallbackPhone}
      fallbackAddress={fallbackAddress}
      fromParam={fromParam}
      higherConfidenceThreshold={constants.config.higherConfidenceThreshold}
      obituaryParserEnabled={constants.featureFlags.obituaryParserEnabled}
      baseUrl={constants.links.baseUrl}
      onNavigateToFamilyTreeSimple={() => navigate('/family-tree')}
      onNavigateToFamilyTreeWithPermalink={(fromPermalink) =>
        navigate(`/family-tree?from=${fromPermalink}`)
      }
      onNavigateToListings={(url) => navigate(url)}
      onOpenExternalReport={(type, permalink) => {
        const reportUrl = `${constants.links.baseUrl}/report/${type}?permalink=${permalink}`;
        window.open(reportUrl, '_blank', 'noopener');
      }}
      isInPartialAddressesVariation={isInPartialAddressesVariation}
      isInStaggeredPhoneUpdateVariation={isInStaggeredPhoneUpdateVariation}
      isPhonePartialAddress={isPhonePartialAddress}
      isInListV2Variation={isInListV2Variation}
      showUsernameSocialMedias={showUsernameSocialMedias}
      isInTaxHistoryVariation={isInTaxHistoryVariation}
      isInPropertySmartInsightsVariation={isInPropertySmartInsightsVariation}
      isInDetailedHistoryVariation={isInDetailedHistoryVariation}
      isInSummaryCardsVariation={isInSummaryCardsVariation}
      isInDamageHistory={isInDamageHistory}
      isInVehicleSpecsVariation={isInVehicleSpecsVariation}
      legitCallerGifUrl={legitCallerGifUrl}
      questionableCallerGifUrl={questionableCallerGifUrl}
      telemarketerCallerGifUrl={telemarketerCallerGifUrl}
      imgTreeOfLife={constants.images.imgTreeOfLife}
      imgInfinity={constants.images.imgInfinity}
      imgPersonSearch={constants.images.imgPersonSearch}
      PersonSearchBanner={PersonSearchBanner}
      ObituarySearchBanner={ObituarySearchBanner}
      getPersonNavLinkData={({
        owner,
        hideCriminalBJLData,
        revealFreeBonusData,
        relatives,
        licensesCount,
      }) =>
        getPersonNavLinkDataV2(
          owner,
          hideCriminalBJLData,
          revealFreeBonusData,
          relatives,
          licensesCount
        )
      }
      getPropertyNavLinkData={getPropertyNavLinkData}
      getPropertyValuesCount={getPropertyValuesCount}
      formatFamilyTreeDataFromPersonReport={
        formatFamilyTreeDataFromPersonReport
      }
      getInitialVehicleReportPreferences={getInitialVehicleReportPreferences}
    />
  );
};
